import type { ReactNode } from 'react';

interface AuthShowcasePanelProps {
  title: string;
  // Başlığın ikinci satırı; altı el çizimi çizgiyle vurgulanır.
  highlight: string;
  description: string;
  features: string[];
  // Madde listesinin altındaki, sağdaki forma işaret eden el yazısı not.
  note?: string;
  // Panelin altına yaslanan içerik (giriş: hesap türleri, kayıt: "Nasıl başlarsın" adımları).
  children?: ReactNode;
}

// Giriş/kayıt sayfalarının sol koyu marka paneli (Giriş / Kayıt tasarımları). Geniş ekranda görünür.
export const AuthShowcasePanel = ({
  title,
  highlight,
  description,
  features,
  note,
  children,
}: AuthShowcasePanelProps) => (
  <aside className='relative hidden flex-col overflow-hidden bg-footer px-14 pt-16 lg:flex'>
    <svg
      aria-hidden='true'
      className='pointer-events-none absolute -right-[120px] -top-[140px]'
      width='520'
      height='520'
      viewBox='0 0 520 520'
      fill='none'
    >
      <path
        d='M260 30C390 22 494 120 488 262c-6 140-104 236-236 228C120 482 28 380 34 250 40 118 140 38 260 30z'
        stroke='var(--public-panel-line)'
        strokeWidth='1.2'
      />
      <path
        d='M262 92C362 86 432 164 428 262c-4 100-76 170-170 166-96-4-158-80-156-172 2-96 66-160 160-164z'
        stroke='rgba(154,166,255,.10)'
        strokeDasharray='3 6'
      />
    </svg>

    <h2 className='relative mt-[22px] text-[44px] font-semibold leading-[1.08] tracking-[-.04em] text-footer-heading'>
      {title}
      <br />
      <span className='relative inline-block text-public-panel-accent'>
        {highlight}
        <svg
          aria-hidden='true'
          className='pointer-events-none absolute -bottom-3 -left-0.5 w-full overflow-visible'
          height='12'
          viewBox='0 0 330 12'
          preserveAspectRatio='none'
        >
          <path
            d='M2 9C50 3 110 12 170 6S280 3 330 8'
            fill='none'
            stroke='hsl(var(--public-panel-accent))'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
        <svg
          aria-hidden='true'
          className='pointer-events-none absolute -right-[30px] -top-1.5 overflow-visible'
          width='26'
          height='26'
          viewBox='0 0 26 26'
        >
          <path
            d='M0 14l6-12M10 16l12-8M12 24h12'
            fill='none'
            stroke='hsl(var(--public-panel-accent))'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </span>
    </h2>
    <p className='relative mt-7 max-w-[430px] text-base leading-[1.6] text-public-panel-muted'>{description}</p>

    <div className='relative mt-9'>
      <ul>
        {features.map((feature) => (
          <li key={feature} className='group relative flex items-center gap-4 pb-[22px] last:pb-0'>
            <span className='relative z-[1] flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full border-[1.5px] border-[var(--public-panel-line-accent)] bg-footer'>
              <svg aria-hidden='true' width='13' height='13' viewBox='0 0 24 24'>
                <path
                  d='M5 12.5l4.5 4.5L19 7.5'
                  fill='none'
                  stroke='hsl(var(--public-panel-accent))'
                  strokeWidth='2.6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
            <span className='text-[15.5px] text-public-panel-bright'>{feature}</span>
            <i
              aria-hidden='true'
              className='absolute bottom-0 left-3.5 top-[30px] w-[1.5px] bg-[var(--public-panel-line-strong)] group-last:hidden'
            />
          </li>
        ))}
      </ul>
      <div aria-hidden='true' className='pointer-events-none absolute left-10 top-full mt-2'>
        <svg className='absolute left-0 top-0 overflow-visible' width='20' height='20' viewBox='0 0 20 20'>
          <path
            d='M2 18C6 10 10 6 18 2M18 2l-8 1M18 2l-2 8'
            fill='none'
            stroke='hsl(var(--public-panel-accent))'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
        {note && (
          <span className='absolute left-[26px] top-2 rotate-[-4deg] whitespace-nowrap font-[Caveat,cursive] text-[23px] leading-none text-public-panel-accent'>
            {note}
          </span>
        )}
      </div>
    </div>

    <div className='min-h-10 flex-1' />
    <div className='relative pb-11'>{children}</div>
  </aside>
);

// Giriş paneli altı: hesap türleri (süslü parantezle gruplu).
export const AuthAccountTypeLegend = ({ rows }: { rows: { label: string; description: string }[] }) => (
  <div className='relative pl-[22px]'>
    {rows.map((row) => (
      <div
        key={row.label}
        className='flex items-center gap-4 border-t border-[var(--public-panel-line)] py-3.5 first:border-t-0'
      >
        <span className='w-[84px] flex-none font-[Geist_Mono,ui-monospace,monospace] text-[11px] font-medium uppercase tracking-[.16em] text-public-panel-accent'>
          {row.label}
        </span>
        <span className='text-[14.5px] text-public-panel-bright'>{row.description}</span>
      </div>
    ))}
    <svg
      aria-hidden='true'
      className='pointer-events-none absolute left-0 top-1.5 overflow-visible'
      width='16'
      height='92'
      viewBox='0 0 16 92'
      preserveAspectRatio='none'
    >
      <path
        d='M14 2C4 2 4 10 4 20S4 40 2 46 4 54 4 66 4 88 14 90'
        fill='none'
        stroke='hsl(var(--public-panel-accent))'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  </div>
);

// Kayıt paneli altı: "Nasıl başlarsın" adımları (Kayıt tasarımları); ilk adım (bulunulan adım) el çizimi
// halkayla işaretlidir, adımlar arasında oklar vardır.
export const AuthSignupSteps = ({ title, here, steps }: { title: string; here: string; steps: string[] }) => (
  <div className='relative pb-0.5'>
    <div
      aria-hidden='true'
      className='pointer-events-none absolute -top-[38px] left-0 rotate-[-5deg] whitespace-nowrap font-[Caveat,cursive] text-2xl leading-none text-public-panel-accent'
    >
      {here}
    </div>
    <div className='mb-4 font-[Geist_Mono,ui-monospace,monospace] text-[11px] font-medium tracking-[.16em] text-public-panel-dim'>
      {title}
    </div>
    <ol className='flex gap-[18px]'>
      {steps.map((step, index) => (
        <li key={step} className='group relative flex-1'>
          <div className='font-[Caveat,cursive] text-[30px] leading-none text-[rgba(244,242,236,.45)] group-first:text-footer-heading'>
            {index + 1}
          </div>
          <div className='mt-1.5 text-[13.5px] leading-[1.35] text-public-panel-dim group-first:text-public-panel-bright'>
            {step}
          </div>
          <svg
            aria-hidden='true'
            className='pointer-events-none absolute -left-3 -top-1.5 hidden h-9 w-11 overflow-visible group-first:block'
            viewBox='0 0 44 34'
            preserveAspectRatio='none'
          >
            <path
              d='M24 4C8 2 0 12 4 22s20 14 32 8 12-24-6-26'
              fill='none'
              stroke='hsl(var(--public-panel-accent))'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <svg
            aria-hidden='true'
            className='pointer-events-none absolute right-1.5 top-2.5 overflow-visible group-last:hidden'
            width='30'
            height='10'
            viewBox='0 0 30 10'
          >
            <path
              d='M0 6C10 2 20 2 30 6M30 6l-6-4M30 6l-6 4'
              fill='none'
              stroke='hsl(var(--public-panel-accent))'
              strokeWidth='1.4'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </li>
      ))}
    </ol>
  </div>
);
